import React, { useState, useEffect } from "react";
import { IoCheckmarkCircleSharp } from "react-icons/io5";
import { doc, updateDoc, increment, arrayUnion } from "firebase/firestore";
import { db } from "../../firebase/firestore";
import { useUser } from "../../context/userContext";

const AdTask = () => {
  const {
    id,
    setBalance,
    setTaskPoints,
    completedDailyTasks,
    setCompletedDailyTasks,
  } = useUser();

  const [adWatched, setAdWatched] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [congrats, setCongrats] = useState(false);
  const [showAdCooldown, setShowAdCooldown] = useState(0);
  const [isAdLoading, setIsAdLoading] = useState(false);
  const [adError, setAdError] = useState(null);

  const task = {
    bonus: 250,
  };

  const generateTaskId = () => `adTask_${new Date().getTime()}`;
  const [taskId, setTaskId] = useState(generateTaskId());

  useEffect(() => {
    if (window.show_9894214) return;
    const existingScript = document.querySelector('script[data-zone="9894214"]');
    if (existingScript) return;

    const tag = document.createElement("script");
    tag.src = "//libtl.com/sdk.js";
    tag.dataset.zone = "9894214";
    tag.dataset.sdk = "show_9894214";
    tag.async = true;
    
    tag.onerror = () => {
      console.error("Failed to load ad script");
      setAdError("Failed to load ad provider. Please refresh the page.");
      setIsAdLoading(false);
    };
    
    document.body.appendChild(tag);

    return () => {
      if (tag.parentNode) {
        document.body.removeChild(tag);
      }
    };
  }, []);

  const showAd = async () => {
    if (showAdCooldown > 0) return;
    
    setIsAdLoading(true);
    setAdError(null);
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log("Showing mock ad for development");
        await new Promise(resolve => setTimeout(resolve, 2000));
        handleAdCompletion();
        return;
      }
      
      if (!window.show_9894214) {
        throw new Error("Ad provider not loaded yet");
      }

      await window.show_9894214();
      handleAdCompletion();
    } catch (error) {
      console.error("Error showing ad:", error);
      setAdError("Failed to load ad. Please try again later.");
    } finally {
      setIsAdLoading(false);
    }
  };

  const handleAdCompletion = () => {
    setAdWatched(true);
    setShowAdCooldown(14400);
    const countdown = setInterval(() => {
      setShowAdCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const claimReward = async () => {
    if (completedDailyTasks.includes(taskId)) {
      alert("You have already claimed this reward.");
      return;
    }

    if (!adWatched) {
      alert("You need to watch an ad before claiming the reward.");
      return;
    }

    setClaiming(true);
    try {
      const userDocRef = doc(db, "telegramUsers", id);
      await updateDoc(userDocRef, {
        balance: increment(task.bonus),
        dailyTasksCompleted: arrayUnion(taskId),
        taskPoints: increment(task.bonus),
      });

      setBalance((prev) => prev + task.bonus);
      setCompletedDailyTasks((prev) => [...prev, taskId]);
      setTaskPoints((prev) => prev + task.bonus);

      setAdWatched(false);
      setCongrats(true);

      setTimeout(() => setCongrats(false), 4000);
      setTaskId(generateTaskId());
    } catch (error) {
      console.error("Error claiming reward:", error);
      alert("An error occurred while claiming your reward. Please try again.");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="mb-6">
      {/* Task Card */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        {/* Task Info */}
        <div className="mb-4">
          <h3 className="font-semibold text-lg text-gray-800 mb-1">Watch an Ad</h3>
          <p className="text-gray-600 text-sm mb-2">
            Watch a short advertisement to earn TASKORI
          </p>
          <div className="text-gray-800 font-medium">
            Reward: +{task.bonus} TASKORI
          </div>
        </div>

        {/* Action Buttons - Side by side */}
        <div className="flex space-x-2">
          <button
            onClick={showAd}
            disabled={showAdCooldown > 0 || isAdLoading}
            className={`flex-1 py-2 rounded-lg font-medium transition-all ${
              showAdCooldown > 0 || isAdLoading
                ? "bg-gray-200 text-gray-500"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {isAdLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading
              </span>
            ) : showAdCooldown > 0 ? (
              `Wait ${showAdCooldown}s`
            ) : (
              "Show Ad"
            )}
          </button>

          <button
            onClick={claimReward}
            disabled={!adWatched || claiming}
            className={`flex-1 py-2 rounded-lg font-medium transition-all ${
              claiming
                ? "bg-gray-200 text-gray-500"
                : adWatched
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-200 text-gray-400"
            }`}
          >
            {claiming ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Claiming
              </span>
            ) : (
              "Claim Reward"
            )}
          </button>
        </div>

        {/* Error Message */}
        {adError && (
          <div className="mt-3 text-xs text-red-500 bg-red-50 p-2 rounded">
            {adError}
          </div>
        )}
      </div>

      {/* Success Notification */}
      {congrats && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
            <IoCheckmarkCircleSharp className="text-green-500 text-xl" />
            <span className="font-medium">Reward claimed successfully!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdTask;
